import { useCallback, useEffect, useRef, useState } from 'react';
import { useAbility } from '@casl/react';
import { AlertTriangle, Bell, Info, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Checkbox } from '../../../components/ui/checkbox';
import { Combobox } from '../../../components/ui/combobox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Textarea } from '../../../components/ui/textarea';
import AccessRestricted from '../../../components/AccessRestricted';
import { AbilityContext } from '../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../constants/permissions.constants';
import { Branch } from '../../../types/lists.types';
import {
    ProcurementApprovalRule,
    ProcurementNotificationEvent,
    ProcurementNotificationUser,
    ProcurementSettings,
    ProcurementUserOption,
} from '../../../types/procurement.types';
import { procurementSettingsApi } from '../../../utils/procurement-api';
import { branchesApi } from '../../../utils/lists-api';

interface ThresholdRow {
    /** 'global' or the branch id — also the lookup key back into the saved rules. */
    key: string;
    branchId: number | null;
    label: string;
    hint: string;
    ruleId: number | null;
    value: string;
    isActive: boolean;
}

/** One "this user receives for this branch" row, held as strings because they are pickers. */
interface AllocationRow {
    /** Local-only identity: a saved row keeps its id, a new one gets a counter, so editing a
     *  row's user never remounts it. */
    key: string;
    userId: string;
    branchId: string;
}

const GLOBAL_ROW_KEY = 'global';

/** The four events the matrix configures. Stable keys — the server jsonb is keyed by them. */
const NOTIFICATION_EVENTS: {
    key: ProcurementNotificationEvent;
    label: string;
    hint: string;
}[] = [
    { key: 'new_purchase_order', label: 'New Purchase Order', hint: 'A request is raised' },
    { key: 'purchase_order_approved', label: 'Purchase Order Approved', hint: 'An approver signs it off' },
    { key: 'new_receipt_logged', label: 'New Receipt Logged', hint: 'A receipt is posted' },
    { key: 'purchase_order_complete', label: 'Purchase Order Complete', hint: 'The last line is received' },
];

const EMPTY_NOTIFICATIONS: Record<ProcurementNotificationEvent, number[]> = {
    new_purchase_order: [],
    purchase_order_approved: [],
    new_receipt_logged: [],
    purchase_order_complete: [],
};

/** A Combobox option — the shape the picker takes, and the shape both choice lists hold. */
interface PickerOption {
    label: string;
    value: string;
    subtitle?: string;
}

/**
 * The choices for one picker: the live options, plus a labelled entry for any id already
 * saved but no longer offered — an inactive branch, a retired user. Without that fallback a
 * stale allocation would render as a blank picker with no way to tell what it points at.
 */
const withSavedFallback = (
    live: PickerOption[],
    saved: { value: string; label: string }[],
) => {
    const known = new Set(live.map((option) => option.value));
    const extra = saved
        .filter((entry) => !known.has(entry.value))
        .map((entry) => ({ value: entry.value, label: `${entry.label} (inactive)` }));
    return [...live, ...extra];
};

/**
 * Procurement settings — the three rules the module lets an admin change: the approval
 * threshold (global and per branch), the receipt photo minimum, and which branches a user
 * receives for.
 *
 * The threshold is the one that needs explaining rather than just editing: it is snapshotted
 * onto a purchase order at submit, so a change here can never rewrite a decision that has
 * already been made. That sentence is on the screen, not just in this comment.
 */
export default function ProcurementSettingsPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.PURCHASE_ORDER_SETTINGS);
    const canUpdate = ability.can(ACTIONS.UPDATE, RESOURCES.PURCHASE_ORDER_SETTINGS);

    const [settings, setSettings] = useState<ProcurementSettings | null>(null);
    const [rows, setRows] = useState<ThresholdRow[]>([]);
    const [allocations, setAllocations] = useState<AllocationRow[]>([]);
    const [userChoices, setUserChoices] = useState<PickerOption[]>([]);
    const [branchChoices, setBranchChoices] = useState<PickerOption[]>([]);
    const [minPhotos, setMinPhotos] = useState('4');
    const [photoHelperText, setPhotoHelperText] = useState('');
    const [notificationUsers, setNotificationUsers] = useState<ProcurementNotificationUser[]>([]);
    const [notifications, setNotifications] = useState<Record<ProcurementNotificationEvent, number[]>>(
        EMPTY_NOTIFICATIONS);
    const [loading, setLoading] = useState(true);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [rulesError, setRulesError] = useState<string | null>(null);
    const [allocationsError, setAllocationsError] = useState<string | null>(null);
    const [photosError, setPhotosError] = useState<string | null>(null);
    const [notificationsError, setNotificationsError] = useState<string | null>(null);
    const [savingRules, setSavingRules] = useState(false);
    const [savingAllocations, setSavingAllocations] = useState(false);
    const [savingPhotos, setSavingPhotos] = useState(false);
    const [savingNotifications, setSavingNotifications] = useState(false);

    /** Keys for rows added on this screen — saved rows keep theirs, so editing one never
     *  remounts the pickers mid-keystroke. */
    const nextAllocationKey = useRef(0);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [settingsData, branches, pickerUsers] = await Promise.all([
                procurementSettingsApi.get(),
                branchesApi.list({ status: 'active' }),
                // Tolerant: the allocation table still renders the saved rows without the
                // picker's user list, so a failed selector read must not blank the screen.
                procurementSettingsApi.listUsers().catch(() => [] as ProcurementUserOption[]),
            ]);

            const byBranch: Record<string, ProcurementApprovalRule | undefined> = {};
            settingsData.approvalRules.forEach((rule) => {
                byBranch[rule.branchId === null ? GLOBAL_ROW_KEY : String(rule.branchId)] = rule;
            });

            const rowShape: Omit<ThresholdRow, 'ruleId' | 'value' | 'isActive'>[] = [
                {
                    key: GLOBAL_ROW_KEY,
                    branchId: null,
                    label: 'Global default',
                    hint: 'Applies to any branch without its own row.',
                },
                ...branches.map((branch: Branch) => ({
                    key: String(branch.id),
                    branchId: branch.id,
                    label: branch.name,
                    hint: 'Overrides the global default for this branch.',
                })),
            ];

            setRows(rowShape.map((row) => {
                const rule = byBranch[row.key];
                return {
                    ...row,
                    ruleId: rule?.id ?? null,
                    value: rule ? String(rule.thresholdAmount) : '0',
                    isActive: rule?.isActive ?? true,
                };
            }));
            setMinPhotos(String(settingsData.minReceiptPhotos));
            setPhotoHelperText(settingsData.photoHelperText ?? '');
            setNotificationUsers(settingsData.notificationUsers ?? []);
            setNotifications({
                ...EMPTY_NOTIFICATIONS,
                ...(settingsData.notifications ?? {}),
            });

            const savedAllocations = settingsData.userBranches ?? [];
            setUserChoices(withSavedFallback(
                pickerUsers.map((user) => ({
                    label: user.name,
                    value: String(user.id),
                    subtitle: user.email,
                })),
                savedAllocations.map((allocation) => ({
                    value: allocation.userId === null ? '' : String(allocation.userId),
                    label: allocation.userName ?? `User ${allocation.userId}`,
                })),
            ));
            setBranchChoices(withSavedFallback(
                branches.map((branch: Branch) => ({ label: branch.name, value: String(branch.id) })),
                savedAllocations.map((allocation) => ({
                    value: allocation.branchId === null ? '' : String(allocation.branchId),
                    label: allocation.branchName ?? `Branch ${allocation.branchId}`,
                })),
            ));
            setAllocations(savedAllocations.map((allocation) => ({
                key: `saved-${allocation.id}`,
                userId: allocation.userId === null ? '' : String(allocation.userId),
                branchId: allocation.branchId === null ? '' : String(allocation.branchId),
            })));
            setSettings(settingsData);
            setLoadError(null);
        } catch (err) {
            // The api client has already toasted; the screen still explains itself.
            setLoadError(err instanceof Error && err.message
                ? err.message
                : 'Failed to load procurement settings');
        } finally {
            setLoading(false);
            setHasLoaded(true);
        }
    }, []);

    useEffect(() => { if (canRead) load(); }, [canRead, load]);

    const addAllocation = () => {
        setAllocations((current) => [
            ...current,
            { key: `new-${nextAllocationKey.current++}`, userId: '', branchId: '' },
        ]);
        setAllocationsError(null);
    };

    const updateAllocation = (key: string, patch: Partial<Omit<AllocationRow, 'key'>>) => {
        setAllocations((current) =>
            current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
        setAllocationsError(null);
    };

    const removeAllocation = (key: string) => {
        setAllocations((current) => current.filter((row) => row.key !== key));
        setAllocationsError(null);
    };

    const saveAllocations = async () => {
        const incomplete = allocations.find((row) => !row.userId || !row.branchId);
        if (incomplete) {
            setAllocationsError(
                'Choose a user and a branch on every row, or remove the rows you do not need.');
            return;
        }

        // Collapsed here as well as rejected server-side: a duplicate pair on screen is a
        // slip, and failing the whole save over it helps nobody. Last pick wins.
        const pairs = new Map<string, { userId: number; branchId: number }>();
        allocations.forEach((row) => {
            pairs.set(`${row.userId}:${row.branchId}`, {
                userId: Number(row.userId),
                branchId: Number(row.branchId),
            });
        });

        try {
            setSavingAllocations(true);
            setAllocationsError(null);
            await procurementSettingsApi.saveUserBranches([...pairs.values()]);
            toast.success('User branch allocations saved');
            await load();
        } catch (err) {
            setAllocationsError(err instanceof Error && err.message
                ? err.message
                : 'Could not save the user branch allocations');
        } finally {
            setSavingAllocations(false);
        }
    };

    const saveRules = async () => {
        const amounts = rows.map((row) => ({ row, amount: Number(row.value) }));
        const invalid = amounts.find(({ amount }) => !Number.isFinite(amount) || amount < 0);
        if (invalid) {
            setRulesError(`Enter a threshold amount for ${invalid.row.label}.`);
            return;
        }

        // Rules for branches that are no longer active are not on screen, so they are sent
        // back exactly as the server has them rather than being dropped by the upsert.
        const renderedBranchIds = rows.map((row) => row.branchId);
        const untouched = (settings?.approvalRules ?? [])
            .filter((rule) => !renderedBranchIds.includes(rule.branchId))
            .map((rule) => ({
                id: rule.id,
                branchId: rule.branchId,
                thresholdAmount: rule.thresholdAmount,
                isActive: rule.isActive,
            }));

        try {
            setSavingRules(true);
            setRulesError(null);
            await procurementSettingsApi.saveApprovalRules([
                ...amounts.map(({ row, amount }) => ({
                    id: row.ruleId,
                    branchId: row.branchId,
                    thresholdAmount: amount,
                    isActive: row.isActive,
                })),
                ...untouched,
            ]);
            toast.success('Approval thresholds saved');
            await load();
        } catch (err) {
            setRulesError(err instanceof Error && err.message
                ? err.message
                : 'Could not save the approval thresholds');
        } finally {
            setSavingRules(false);
        }
    };

    const savePhotos = async () => {
        const count = Number(minPhotos);
        if (!Number.isInteger(count) || count < 0 || count > 8) {
            setPhotosError('Minimum photos must be a whole number between 0 and 8.');
            return;
        }

        try {
            setSavingPhotos(true);
            setPhotosError(null);
            await procurementSettingsApi.update({
                minReceiptPhotos: count,
                photoHelperText,
            });
            toast.success('Receipt photo rule saved');
            await load();
        } catch (err) {
            setPhotosError(err instanceof Error && err.message
                ? err.message
                : 'Could not save the receipt photo rule');
        } finally {
            setSavingPhotos(false);
        }
    };

    const toggleNotification = (event: ProcurementNotificationEvent, userId: number) => {
        setNotifications((current) => {
            const ids = new Set(current[event] ?? []);
            if (ids.has(userId)) ids.delete(userId);
            else ids.add(userId);
            return { ...current, [event]: [...ids] };
        });
        setNotificationsError(null);
    };

    const saveNotifications = async () => {
        try {
            setSavingNotifications(true);
            setNotificationsError(null);
            await procurementSettingsApi.saveNotifications(notifications);
            toast.success('Notification recipients saved');
            await load();
        } catch (err) {
            setNotificationsError(err instanceof Error && err.message
                ? err.message
                : 'Could not save the notification recipients');
        } finally {
            setSavingNotifications(false);
        }
    };

    if (!canRead) return <AccessRestricted />;

    if (loading && !hasLoaded) {
        return (
            <div className="space-y-4 animate-pulse max-w-3xl mx-auto px-4 sm:px-6 py-8">
                {[0, 1].map((i) => <div key={i} className="h-48 bg-gray-300 rounded-lg" />)}
            </div>
        );
    }

    if (loadError && !settings) {
        return (
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
                <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                        <AlertTriangle size={28} className="mx-auto text-destructive mb-3" />
                        <p className="font-medium">Could not load procurement settings</p>
                        <p className="text-sm text-muted-foreground mt-1">{loadError}</p>
                        <Button variant="outline" className="mt-4" onClick={load}>Try again</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
            <div>
                <h1 className="text-2xl font-bold">Procurement settings</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Who needs approval, what a receipt must prove, and who is emailed.
                </p>
                {!canUpdate && (
                    <p className="text-sm text-amber-700 mt-2">
                        You do not have permission to change these settings — this page is read-only.
                    </p>
                )}
                {loadError && settings && (
                    <p className="text-sm text-amber-700 mt-2">
                        Showing the last settings we loaded — {loadError}
                    </p>
                )}
            </div>

            <Tabs defaultValue="general" className="space-y-4">
                <TabsList className="w-full sm:w-auto">
                    <TabsTrigger value="general">General</TabsTrigger>
                    <TabsTrigger value="notifications">Notifications</TabsTrigger>
                </TabsList>

                <TabsContent value="general" className="space-y-6">
            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold">Approval thresholds</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        A purchase order at or above this amount is sent for approval instead of
                        going straight to Approved.
                    </p>

                    <div className="mt-4 divide-y divide-border">
                        {rows.map((row) => (
                            <div
                                key={row.key}
                                className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:gap-6"
                            >
                                <div className="min-w-0 md:flex-1">
                                    <p className="text-sm font-medium">{row.label}</p>
                                    <p className="text-xs text-muted-foreground">{row.hint}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="relative flex-1 md:w-40">
                                        <span className="absolute left-3 top-2.5 text-sm text-muted-foreground">
                                            $
                                        </span>
                                        <Input
                                            type="number"
                                            inputMode="decimal"
                                            min={0}
                                            step="0.01"
                                            className="pl-7 text-right tabular-nums"
                                            value={row.value}
                                            disabled={!canUpdate}
                                            aria-label={`${row.label} approval threshold`}
                                            onChange={(e) => setRows((current) => current.map((r) =>
                                                r.key === row.key ? { ...r, value: e.target.value } : r))}
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            checked={row.isActive}
                                            disabled={!canUpdate}
                                            aria-label={`${row.label} threshold active`}
                                            onCheckedChange={(checked) => setRows((current) =>
                                                current.map((r) =>
                                                    r.key === row.key ? { ...r, isActive: checked } : r))}
                                        />
                                        <span className="text-xs text-muted-foreground">
                                            {row.isActive ? 'Active' : 'Off'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {rows.length === 1 && (
                        <p className="mt-2 text-sm text-muted-foreground">
                            No active branches yet — the global default applies to every branch.
                        </p>
                    )}

                    <div className="mt-4 flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                        <Info size={16} className="mt-0.5 shrink-0" />
                        <p>
                            The threshold is copied onto each purchase order when it is submitted.
                            Changing it here only affects orders submitted afterwards — an order
                            already waiting for approval keeps the amount it was measured against.
                        </p>
                    </div>

                    {rulesError && <p className="mt-3 text-sm text-destructive">{rulesError}</p>}

                    <div className="mt-4 flex flex-col md:flex-row md:justify-end">
                        <Button onClick={saveRules} disabled={!canUpdate || savingRules} className="w-full md:w-auto">
                            {savingRules ? 'Saving…' : 'Save thresholds'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold">User branch allocation</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Which branch a person receives for. Someone allocated here sees only that
                        branch's purchase orders in the receiving queue; anyone with no row is
                        scoped by the yards they belong to instead.
                    </p>

                    <div className="mt-4 space-y-3">
                        {allocations.map((row) => (
                            <div key={row.key} className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
                                <div className="min-w-0 md:flex-1">
                                    <Combobox
                                        options={userChoices}
                                        value={row.userId || undefined}
                                        onChange={(value) => updateAllocation(row.key, { userId: value ?? '' })}
                                        placeholder="Select a user"
                                        searchPlaceholder="Search people…"
                                        emptyMessage="No user matches that search."
                                        className="w-full"
                                    />
                                </div>
                                <div className="min-w-0 md:w-64">
                                    <Combobox
                                        options={branchChoices}
                                        value={row.branchId || undefined}
                                        onChange={(value) => updateAllocation(row.key, { branchId: value ?? '' })}
                                        placeholder="Select a branch"
                                        searchable={false}
                                        emptyMessage="No branch available."
                                        className="w-full"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    disabled={!canUpdate}
                                    aria-label="Remove this allocation"
                                    onClick={() => removeAllocation(row.key)}
                                >
                                    <Trash2 size={16} />
                                </Button>
                            </div>
                        ))}

                        {allocations.length === 0 && (
                            <p className="text-sm text-muted-foreground">
                                No one is allocated yet — every receiver is scoped by their yards.
                            </p>
                        )}
                    </div>

                    <Button type="button" variant="outline" size="sm" className="mt-3"
                            disabled={!canUpdate} onClick={addAllocation}>
                        <Plus size={16} className="mr-1.5" />Add allocation
                    </Button>

                    <div className="mt-4 flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                        <Info size={16} className="mt-0.5 shrink-0" />
                        <p>
                            A user can be allocated to more than one branch. An allocation
                            replaces the yard-based scope, so allocating hides every other
                            branch's orders from that person's receiving queue. Saving sends the
                            whole list — removing a row here removes the allocation.
                        </p>
                    </div>

                    {allocationsError && (
                        <p className="mt-3 text-sm text-destructive">{allocationsError}</p>
                    )}

                    <div className="mt-4 flex flex-col md:flex-row md:justify-end">
                        <Button onClick={saveAllocations} disabled={!canUpdate || savingAllocations}
                                className="w-full md:w-auto">
                            {savingAllocations ? 'Saving…' : 'Save allocations'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold">Receipt photos</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        How many photos a receipt must carry before it can be posted. The count is
                        enforced when the receipt is posted, not in the browser.
                    </p>

                    <div className="mt-4 space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="min-receipt-photos">Minimum photos per receipt (0–8)</Label>
                            <Input
                                id="min-receipt-photos"
                                type="number"
                                inputMode="numeric"
                                min={0}
                                max={8}
                                step="1"
                                className="w-full md:w-32"
                                value={minPhotos}
                                disabled={!canUpdate}
                                onChange={(e) => setMinPhotos(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="photo-helper-text">Helper text shown while capturing</Label>
                            <Textarea
                                id="photo-helper-text"
                                rows={3}
                                value={photoHelperText}
                                disabled={!canUpdate}
                                placeholder="Photograph the docket and every damaged item."
                                onChange={(e) => setPhotoHelperText(e.target.value)}
                            />
                        </div>
                    </div>

                    {photosError && <p className="mt-3 text-sm text-destructive">{photosError}</p>}

                    <div className="mt-4 flex flex-col md:flex-row md:justify-end">
                        <Button onClick={savePhotos} disabled={!canUpdate || savingPhotos} className="w-full md:w-auto">
                            {savingPhotos ? 'Saving…' : 'Save photo rule'}
                        </Button>
                    </div>
                </CardContent>
            </Card>
                </TabsContent>

                <TabsContent value="notifications" className="space-y-6">
                    <Card>
                        <CardContent className="p-4 md:p-6">
                            <div className="flex items-start gap-3">
                                <span className="w-9 h-9 rounded-md border border-border bg-muted/40 flex items-center justify-center shrink-0">
                                    <Bell size={16} className="text-muted-foreground" />
                                </span>
                                <div>
                                    <h2 className="font-semibold">Notifications</h2>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Choose who receives an email for each purchase order event.
                                        Only users with access to the Purchase Order module are listed.
                                    </p>
                                </div>
                            </div>

                            {notificationUsers.length === 0 ? (
                                <p className="mt-4 rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
                                    No active users currently have access to the Purchase Order module.
                                </p>
                            ) : (
                                <div className="mt-4 overflow-x-auto">
                                    <table className="w-full min-w-[44rem] border-collapse text-sm">
                                        <thead>
                                            <tr className="border-b border-border">
                                                <th className="py-3 pr-3 text-left font-medium">User</th>
                                                {NOTIFICATION_EVENTS.map((event) => (
                                                    <th key={event.key}
                                                        className="px-2 py-3 text-center align-bottom font-medium">
                                                        <span className="block">{event.label}</span>
                                                        <span className="mt-0.5 block text-[11px] font-normal text-muted-foreground">
                                                            {event.hint}
                                                        </span>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {notificationUsers.map((user) => (
                                                <tr key={user.id}
                                                    className="border-b border-border last:border-0">
                                                    <td className="py-3 pr-3">
                                                        <p className="font-medium">{user.name}</p>
                                                        <p className="text-xs text-muted-foreground">{user.email}</p>
                                                        {(user.jobRole || user.userRole) && (
                                                            <p className="mt-0.5 text-[11px] text-muted-foreground">
                                                                {user.jobRole || user.userRole}
                                                            </p>
                                                        )}
                                                    </td>
                                                    {NOTIFICATION_EVENTS.map((event) => (
                                                        <td key={event.key} className="px-2 py-3 text-center">
                                                            <Checkbox
                                                                checked={(notifications[event.key] ?? [])
                                                                    .includes(user.id)}
                                                                disabled={!canUpdate}
                                                                aria-label={`${user.name} — ${event.label}`}
                                                                onCheckedChange={() =>
                                                                    toggleNotification(event.key, user.id)}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className="mt-4 flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                                <Info size={16} className="mt-0.5 shrink-0" />
                                <p>
                                    Emails show the order's header — supplier, dates, PO number,
                                    requester, approver, receiver and total — but never the line
                                    items. An empty column means nobody is emailed for that event.
                                </p>
                            </div>

                            {notificationsError && (
                                <p className="mt-3 text-sm text-destructive">{notificationsError}</p>
                            )}

                            <div className="mt-4 flex flex-col md:flex-row md:justify-end">
                                <Button onClick={saveNotifications}
                                        disabled={!canUpdate || savingNotifications}
                                        className="w-full md:w-auto">
                                    {savingNotifications ? 'Saving…' : 'Save notifications'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
